/*
 * CYBERFEST navigation dropdown (sections/header-navigation.liquid loads this only while the item is on).
 * Desktop: split button (label link + chevron button). The panel opens on mouse hover and on click, Enter or
 * Space on the chevron; it closes on Escape, a click outside, focus leaving, and when the pointer leaves
 * (unless it was opened with a click or key, which pins it). Mobile: the drawer group heading is a plain
 * disclosure button. Independent of header-navigation.js (nothing here touches the subnav or mega panels).
 */
(() => {
  const CLOSE_DELAY = 180;

  const initDesktop = (item) => {
    if (item.dataset.cfNavReady) return;
    const toggle = item.querySelector('[data-cf-nav-toggle]');
    const panel = item.querySelector('[data-cf-nav-panel]');
    if (!toggle || !panel) return;
    item.dataset.cfNavReady = '1';

    // The banner is lazy (the panel is hidden), which made the first hover wait for the download. Once the page has
    // loaded and gone idle, fetch it so the first open is instant; touch-only devices skip this.
    const warmImages = () => {
      if (!window.matchMedia('(hover: hover)').matches) return;
      panel.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    };
    const scheduleWarm = () => ('requestIdleCallback' in window ? window.requestIdleCallback(warmImages, { timeout: 3000 }) : window.setTimeout(warmImages, 1500));
    if (document.readyState === 'complete') scheduleWarm();
    else window.addEventListener('load', scheduleWarm, { once: true });

    let pinned = false;
    let closeTimer = null;

    const isOpen = () => !panel.hidden;
    const clearTimer = () => {
      if (closeTimer) window.clearTimeout(closeTimer);
      closeTimer = null;
    };
    const open = () => {
      clearTimer();
      if (isOpen()) return;
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      item.classList.add('is-open');
    };
    const close = ({ focusToggle = false } = {}) => {
      clearTimer();
      pinned = false;
      if (isOpen()) {
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        item.classList.remove('is-open');
      }
      if (focusToggle) toggle.focus();
    };
    const scheduleClose = () => {
      clearTimer();
      closeTimer = window.setTimeout(() => close(), CLOSE_DELAY);
    };

    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') open();
    });
    item.addEventListener('pointerleave', (event) => {
      // Keyboard focus inside the panel keeps it open: closing under it would drop focus to <body>. Only the panel
      // counts: a mouse click leaves focus on the chevron, which must not keep later hover-opens pinned.
      if (event.pointerType === 'mouse' && !pinned && isOpen() && !panel.contains(document.activeElement)) scheduleClose();
    });

    toggle.addEventListener('click', () => {
      if (isOpen() && !pinned) {
        // Opened by hovering: a click keeps it open (pinned) instead of flicking it shut under the pointer.
        pinned = true;
        return;
      }
      if (isOpen()) close();
      else {
        pinned = true;
        open();
      }
    });

    toggle.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      pinned = true;
      open();
      panel.querySelector('a[href]')?.focus();
    });

    item.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isOpen()) return;
      event.preventDefault();
      event.stopPropagation();
      close({ focusToggle: item.contains(document.activeElement) });
    });

    item.addEventListener('focusout', (event) => {
      if (event.relatedTarget && item.contains(event.relatedTarget)) return;
      window.setTimeout(() => {
        if (!item.contains(document.activeElement)) close();
      }, 0);
    });

    document.addEventListener('pointerdown', (event) => {
      if (isOpen() && !item.contains(event.target)) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) close();
    });
    // The desktop row is hidden when the header switches to the mobile layout (resize) or goes compact while
    // scrolling (the primary list is display:none then): reset the state so a pinned panel does not reappear open.
    const closeIfHidden = () => {
      if (isOpen() && !item.offsetParent) close();
    };
    window.addEventListener('resize', closeIfHidden);
    window.addEventListener('scroll', closeIfHidden, { passive: true });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-cf-nav]').forEach(initDesktop);
  };

  // Mobile drawer group (the drawer is moved to <body>, so delegate from the document).
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-cf-nav-group-toggle]');
    if (!button) return;
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    const willOpen = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    panel.hidden = !willOpen;
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
